import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  Body,
  Container,
  Head,
  Heading,
  Html,
  Img,
  Preview,
  Section,
  Text,
} from '@react-email/components';

export interface LeadLostEmailProps {
  customerName: string;
  /** Plain-text personalized message written by the sales rep. Newlines are preserved. */
  personalMessage?: string;
  senderName?: string;
}

export const leadLostEmail = async (
  params: LeadLostEmailProps,
): Promise<{ subject: string; html: string }> => {
  const { customerName, personalMessage, senderName } = params;
  const greetingName = (customerName || '').trim().split(/\s+/)[0] || 'there';
  const subject = `Still here when you're ready — Optic Truck Works`;

  const emailComponent = (
    <Html>
      <Head />
      <Preview>Whenever the timing is right, Optic Truck Works will be here.</Preview>
      <Body
        style={{
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          backgroundColor: '#f5f5f5',
          margin: '0',
          padding: '0',
        }}
      >
        <Container
          style={{
            maxWidth: '600px',
            margin: '0 auto',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginTop: '40px',
            marginBottom: '40px',
          }}
        >
          <Section
            style={{
              padding: '32px',
              textAlign: 'center' as const,
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Img
              src="https://firebasestorage.googleapis.com/v0/b/otw-production-91ffb.appspot.com/o/public%2FLogoVariation-OpticTruckWorks-05.png?alt=media&token=c3428c05-276c-4d32-ac33-a7d9d51fe380"
              alt="Optic Truck Works Logo"
              style={{ maxWidth: '200px', height: 'auto', margin: '0 auto' }}
            />
          </Section>

          <Section style={{ padding: '32px' }}>
            <Heading
              style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#c62828',
                marginBottom: '24px',
                textAlign: 'center' as const,
              }}
            >
              Whenever the timing is right
            </Heading>

            <Text
              style={{
                fontSize: '16px',
                color: '#333333',
                lineHeight: '1.6',
                marginBottom: '16px',
              }}
            >
              Hi {greetingName},
            </Text>

            <Text
              style={{
                fontSize: '16px',
                color: '#333333',
                lineHeight: '1.6',
                marginBottom: '24px',
              }}
            >
              We totally understand things didn’t come together this time, and that’s okay — buying a truck is
              a big decision and the timing has to be right. We just wanted to leave the door open and let you
              know we’re still here whenever you’re ready to talk.
            </Text>

            {personalMessage && personalMessage.trim().length > 0 && (
              <Text
                style={{
                  fontSize: '16px',
                  color: '#333333',
                  lineHeight: '1.6',
                  marginBottom: '24px',
                  whiteSpace: 'pre-line' as const,
                }}
              >
                {personalMessage}
              </Text>
            )}

            <Text
              style={{
                fontSize: '16px',
                color: '#333333',
                lineHeight: '1.6',
                marginBottom: '24px',
              }}
            >
              No pressure at all — just reply to this email if you ever want to revisit your search, talk
              through different options, or have us keep an eye out for the right truck. We’re happy to help
              whenever it makes sense.
            </Text>

            <Text
              style={{
                fontSize: '16px',
                color: '#666666',
                lineHeight: '1.6',
                borderTop: '1px solid #e0e0e0',
                paddingTop: '24px',
                marginTop: '32px',
              }}
            >
              Take care,
              <br />
              {senderName ? `${senderName} — ` : ''}The Optic Truck Works Team
            </Text>
          </Section>
        </Container>
      </Body>
    </Html>
  );

  const html = `<!DOCTYPE html>${renderToStaticMarkup(emailComponent)}`;
  return { subject, html };
};
